import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import { AuthProvider } from './contexts/AuthContext'
import InactivityDetectorWrapper from './components/InactivityDetectorWrapper'

// LoginPage and NotFoundPage stay eager — they're tiny entry/catch-all screens
// (Login shows before auth, so we don't want a loading flash there). Every
// other page is lazy-loaded so each becomes its own chunk. Previously all 34
// pages — including the heavy report, Sanity, and invoice-print pages — were
// bundled into a single ~721KB JS file loaded on every admin login.
import LoginPage from './pages/LoginPage'
import NotFoundPage from './pages/NotFoundPage'

const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const UsersPage = lazy(() => import('./pages/UsersPage'))
const RolesPage = lazy(() => import('./pages/RolesPage'))
const ProductsPage = lazy(() => import('./pages/ProductsPage'))
const EditProductPage = lazy(() => import('./pages/EditProductPage'))
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'))
const SourcesPage = lazy(() => import('./pages/SourcesPage'))
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'))
const InvoiceFormPage = lazy(() => import('./pages/InvoiceFormPage'))
const InvoicePrintPage = lazy(() => import('./pages/InvoicePrintPage'))
const CouponsPage = lazy(() => import('./pages/CouponsPage'))
const ProductImportPage = lazy(() => import('./pages/ProductImportPage'))
const CategoriesImportPage = lazy(() => import('./pages/CategoriesImportPage'))
const BackMarketPage = lazy(() => import('./pages/BackMarketPage'))
const EbayPage = lazy(() => import('./pages/EbayPage'))
const InternalOrdersPage = lazy(() => import('./pages/InternalOrdersPage'))
const BackMarketOrdersPage = lazy(() => import('./pages/BackMarketOrdersPage'))
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'))
const TrackPage = lazy(() => import('./pages/TrackPage'))
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'))
const SyncLogsPage = lazy(() => import('./pages/SyncLogsPage'))
const SyncSchedulesPage = lazy(() => import('./pages/SyncSchedulesPage'))
const SalesReportPage = lazy(() => import('./pages/SalesReportPage'))
const F8InternalSalesReportPage = lazy(() => import('./pages/F8InternalSalesReportPage'))
const BackMarketSalesReportPage = lazy(() => import('./pages/BackMarketSalesReportPage'))
const InventoryReportPage = lazy(() => import('./pages/InventoryReportPage'))
const GeneralSettingsPage = lazy(() => import('./pages/GeneralSettingsPage'))
const ShippingMethodsPage = lazy(() => import('./pages/ShippingMethodsPage'))
const SecuritySettingsPage = lazy(() => import('./pages/SecuritySettingsPage'))
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage'))
const SlideManagementPage = lazy(() => import('./pages/SlideManagementPage'))
const BannerManagementPage = lazy(() => import('./pages/BannerManagementPage'))
const PoliciesPage = lazy(() => import('./pages/PoliciesPage'))
const SEOManagementPage = lazy(() => import('./pages/SEOManagementPage'))

// Shown in the content area while a lazy page chunk loads. Inside Layout, so
// the sidebar/header stay put during navigation.
function PageFallback() {
  return (
    <div className="flex items-center justify-center py-24 text-gray-500">
      Loading…
    </div>
  )
}

function App() {
  // Project-specific .env files - each project has its own configuration
  return (
    <AuthProvider>
      <InactivityDetectorWrapper />
      <div className="min-h-screen bg-gray-50">
        <Routes>
          {/* Public routes */}
          <Route path="/admin/login" element={<LoginPage />} />
          
          {/* Protected admin routes */}
          <Route 
            path="/admin/*" 
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageFallback />}>
                  <Routes>
                    {/* Dashboard */}
                    <Route path="dashboard" element={<DashboardPage />} />
                    
                    {/* Users - requires user management permissions */}
                    <Route 
                      path="users" 
                      element={
                        <ProtectedRoute requiredPermissions={['users:read']}>
                          <UsersPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="roles" 
                      element={
                        <ProtectedRoute requiredPermissions={['roles:read']}>
                          <RolesPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Products - requires product management permissions */}
                    <Route
                      path="products"
                      element={
                        <ProtectedRoute requiredPermissions={['products:read']}>
                          <ProductsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="products/:id/edit"
                      element={
                        <ProtectedRoute requiredPermissions={['products:update']}>
                          <EditProductPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="categories"
                      element={
                        <ProtectedRoute requiredPermissions={['categories:read']}>
                          <CategoriesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="sources"
                      element={
                        <ProtectedRoute requiredPermissions={['products:read']}>
                          <SourcesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="invoices"
                      element={
                        <ProtectedRoute requiredPermissions={['orders:read']}>
                          <InvoicesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="invoices/new"
                      element={
                        <ProtectedRoute requiredPermissions={['orders:read']}>
                          <InvoiceFormPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="invoices/:id/edit"
                      element={
                        <ProtectedRoute requiredPermissions={['orders:read']}>
                          <InvoiceFormPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="invoices/:id/print"
                      element={
                        <ProtectedRoute requiredPermissions={['orders:read']}>
                          <InvoicePrintPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="products/import" 
                      element={
                        <ProtectedRoute requiredPermissions={['products:create']}>
                          <ProductImportPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="categories/import" 
                      element={
                        <ProtectedRoute requiredPermissions={['categories:create']}>
                          <CategoriesImportPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="coupons" 
                      element={
                        <ProtectedRoute requiredPermissions={['admins:read']}>
                          <CouponsPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="ebay"
                      element={
                        <ProtectedRoute requiredPermissions={['products:read']}>
                          <EbayPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="backmarket" 
                      element={
                        <ProtectedRoute requiredPermissions={['products:read']}>
                          <BackMarketPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Orders - requires order management permissions */}
                    <Route 
                      path="orders" 
                      element={
                        <ProtectedRoute requiredPermissions={['orders:read']}>
                          <InternalOrdersPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="orders/backmarket" 
                      element={
                        <ProtectedRoute requiredPermissions={['orders:read']}>
                          <BackMarketOrdersPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="orders/:id" 
                      element={
                        <ProtectedRoute requiredPermissions={['orders:read']}>
                          <OrderDetailPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="track/:trackingNumber" 
                      element={
                        <ProtectedRoute requiredPermissions={['orders:read']}>
                          <TrackPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Reviews - requires review management permissions */}
                    <Route 
                      path="reviews" 
                      element={
                        <ProtectedRoute requiredPermissions={['reviews:read']}>
                          <ReviewsPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Sync - requires admin permissions */}
                    <Route 
                      path="sync/logs" 
                      element={
                        <ProtectedRoute requiredPermissions={['admins:read']}>
                          <SyncLogsPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="sync/schedules" 
                      element={
                        <ProtectedRoute requiredPermissions={['admins:read']}>
                          <SyncSchedulesPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Reports - requires read permissions */}
                    <Route 
                      path="reports/sales" 
                      element={
                        <ProtectedRoute requiredPermissions={['orders:read']}>
                          <SalesReportPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="reports/sales/f8-internal" 
                      element={
                        <ProtectedRoute requiredPermissions={['orders:read']}>
                          <F8InternalSalesReportPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="reports/sales/backmarket" 
                      element={
                        <ProtectedRoute requiredPermissions={['orders:read']}>
                          <BackMarketSalesReportPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="reports/inventory" 
                      element={
                        <ProtectedRoute requiredPermissions={['products:read']}>
                          <InventoryReportPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Settings - requires admin permissions */}
                    <Route 
                      path="seo" 
                      element={
                        <ProtectedRoute>
                          <SEOManagementPage />
                        </ProtectedRoute>
                      } 
                    />

                    <Route 
                      path="settings/general" 
                      element={
                        <ProtectedRoute requiredPermissions={['settings:read']}>
                          <GeneralSettingsPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="settings/shipping-methods" 
                      element={
                        <ProtectedRoute requiredPermissions={['settings:read']}>
                          <ShippingMethodsPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="settings/security" 
                      element={
                        <ProtectedRoute>
                          <SecuritySettingsPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="audit-logs" 
                      element={
                        <ProtectedRoute requiredPermissions={['admins:read']}>
                          <AuditLogsPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Slide Management - requires content management permissions */}
                    <Route 
                      path="slides" 
                      element={
                        <ProtectedRoute requiredPermissions={['content:read']}>
                          <SlideManagementPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Banner Management - requires content management permissions */}
                    <Route 
                      path="banners" 
                      element={
                        <ProtectedRoute requiredPermissions={['content:read']}>
                          <BannerManagementPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Policy Management - requires content management permissions */}
                    <Route 
                      path="policies" 
                      element={
                        <ProtectedRoute requiredPermissions={['content:manage']}>
                          <PoliciesPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Default redirect for admin routes */}
                    <Route index element={<Navigate to="dashboard" replace />} />
                    
                    {/* Catch all for admin routes - show 404 page */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
          
          {/* Catch all - show 404 page */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </AuthProvider>
  )
}

export default App
